import { useEffect, useState } from "react";
import axios from "axios";
import { Link } from "react-router-dom";
import { ArrowRight, CalendarDays, Clock3, Send } from "lucide-react";
import { Reveal } from "@/components/Reveal";
import { SectionHeading } from "@/components/SectionHeading";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { toast } from "@/components/ui/sonner";
import { SEO } from "@/components/SEO";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

function NewsletterForm() {
  const [email, setEmail] = useState("");
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!email) return;
    setLoading(true);
    try {
      await axios.post(`${API}/newsletter/subscribe`, { email });
      toast.success("Inscription réussie ! Vous recevrez nos prochains articles.");
      setEmail("");
    } catch (err) {
      const msg = err.response?.status === 409
        ? "Cette adresse est déjà inscrite."
        : "Une erreur est survenue, veuillez réessayer.";
      toast.error(msg);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="mt-20 rounded-3xl border border-border/70 bg-card/60 p-8 sm:p-10" data-testid="newsletter-section">
      <SEO title="Actualités" description="Articles et analyses Intellix sur la transformation numérique des PME : Microsoft Copilot, Purview, Zero Trust, SOC, IA générative, souveraineté des données et hébergement." path="/actualites" />
      <div className="mx-auto max-w-2xl text-center">
        <p className="text-sm font-semibold uppercase tracking-[0.3em] text-primary">Newsletter</p>
        <h3 className="mt-3 font-display text-2xl font-semibold tracking-tight sm:text-3xl">
          Restez informé de nos dernières publications
        </h3>
        <p className="mt-4 text-sm leading-relaxed text-muted-foreground">
          Recevez nos analyses et conseils pratiques directement dans votre boîte mail. Pas de spam, uniquement du contenu utile.
        </p>
        <form onSubmit={handleSubmit} className="mt-8 flex flex-col gap-3 sm:flex-row sm:gap-2" data-testid="newsletter-form">
          <Input
            type="email"
            required
            placeholder="votre@email.fr"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            className="h-12 flex-1 rounded-full border-border/70 bg-background/50 px-5 text-sm"
            data-testid="newsletter-email-input"
          />
          <Button type="submit" disabled={loading} className="h-12 rounded-full px-8 font-semibold" data-testid="newsletter-submit-btn">
            {loading ? "..." : <><Send className="h-4 w-4" /> S'inscrire</>}
          </Button>
        </form>
      </div>
    </div>
  );
}

export default function Blog() {
  const [posts, setPosts] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    axios.get(`${API}/blog`).then((response) => setPosts(response.data)).finally(() => setLoading(false));
  }, []);

  return (
    <div className="px-5 pb-24 pt-36 sm:px-8 lg:pt-44" data-testid="blog-page">
      <div className="mx-auto max-w-7xl">
        <SectionHeading
          eyebrow="Actualités"
          title="Repères pratiques pour piloter votre transformation numérique."
          description="Des analyses courtes et actionnables sur la cybersécurité, le Cloud Microsoft, l’IA générative et l’automatisation."
        />
        <div className="mt-16 grid gap-5 lg:grid-cols-3" data-testid="blog-posts-grid">
          {loading
            ? [1, 2, 3].map((item) => <div key={item} className="h-72 animate-pulse rounded-3xl border border-border/70 bg-card/60" data-testid={`blog-loading-card-${item}`} />)
            : posts.map((post, index) => (
                <Reveal key={post.id} delay={index * 0.08}>
                  <Link to={`/actualites/${post.slug}`} className="block h-full">
                    <article className="flex h-full flex-col rounded-3xl border border-border/70 bg-card/60 p-7 transition-transform duration-300 hover:-translate-y-1 hover:border-primary/50" data-testid={`blog-post-${post.slug}`}>
                      <Badge variant="outline" className="w-fit border-primary/40 text-primary" data-testid={`blog-category-${post.slug}`}>{post.category}</Badge>
                      <h2 className="mt-7 font-display text-2xl font-semibold tracking-tight">{post.title}</h2>
                      <p className="mt-4 flex-1 text-sm leading-relaxed text-muted-foreground">{post.excerpt}</p>
                      <div className="mt-8 flex items-center justify-between">
                        <div className="flex items-center gap-5 text-xs text-muted-foreground">
                          <span className="inline-flex items-center gap-2" data-testid={`blog-date-${post.slug}`}>
                            <CalendarDays className="h-4 w-4" aria-hidden="true" />
                            {new Intl.DateTimeFormat("fr-FR", { day: "numeric", month: "long", year: "numeric" }).format(new Date(post.published_at))}
                          </span>
                          <span className="inline-flex items-center gap-2" data-testid={`blog-read-time-${post.slug}`}>
                            <Clock3 className="h-4 w-4" aria-hidden="true" /> {post.read_time}
                          </span>
                        </div>
                        <ArrowRight className="h-4 w-4 text-primary opacity-0 transition-opacity group-hover:opacity-100" />
                      </div>
                    </article>
                  </Link>
                </Reveal>
              ))}
        </div>

        <NewsletterForm />
      </div>
    </div>
  );
}
